import { createContext, useContext, useReducer, ReactNode } from "react";
import type { StepId } from "@/domains/quiz/StepDefinitions";
import { PersistedStateV3 } from "../domains/storage/PersistSchema";

type GlobalStep = StepId | "results";

/** Household size cap — must match the SET_DOGS_COUNT clamp and the count question's range. */
export const MAX_DOGS = 5;

type QuizAction =
    | { type: "HYDRATE"; payload: PersistedStateV3 }
    | { type: "UPDATE_DOG_FORM"; dogId: string; payload: any }
    | { type: "SET_ACTIVE_DOG"; dogId: string | null }
    | { type: "SET_DOG_STEP"; dogId: string; step: StepId }
    | { type: "SET_GLOBAL_STEP"; step: GlobalStep }
    | { type: "SET_TREATS"; dogId: string; payload: any }
    | { type: "SET_RESULTS_STEP"; step: number }
    | { type: "ADD_DOG" }
    | { type: "REMOVE_DOG"; dogId: string }
    | { type: "SET_DOGS_COUNT"; count: number }
    | { type: "SET_DELIVERY_DATES"; deliveryDate?: string; shipDate?: string };

export interface QuizState extends PersistedStateV3 { }

function createBlankDogForm(): any {
    return {
        name: "",
        breed: "",
        sex: "",
        years: "",
        months: "",
        weightKg: "",
        bodyCondition: "",
        reproduction: "",
        allergies: [],
        healthIssues: [],
        healthIssuesOther: "",
        currentFood: [],
        currentFoodBrand: "",
        triedFresh: "",
        fussiness: "",
        feedingGoal: "",
        activityLevel: "",
        treatFrequency: "",
        selectedPackSize: "",
        selectedPlanDays: 0,
        christmasSpecialVariantId: "",
        christmasSpecialQuantity: 0,
        mealSelection: [],
        mealSelectedPacks: 0,
        mealRecommendedPacks: 0,
        // Issue #53: consent starts pre-ticked (client decision; compliance
        // risk is logged on the ticket). The lead-capture screen shows the
        // matching pre-ticked checkbox, so state and UI agree.
        marketingOptIn: true,
        excludedFromBox: false,
    };
}

function safeUUID(): string {
    try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch { }
    return "dog-" + Math.random().toString(36).slice(2, 10);
}

const QuizContext = createContext<{
    state: QuizState;
    dispatch: React.Dispatch<QuizAction>;
} | null>(null);

function quizReducer(state: QuizState, action: QuizAction): QuizState {
    switch (action.type) {
        case "HYDRATE": {
            return action.payload;
        }

        case "UPDATE_DOG_FORM": {
            return {
                ...state,
                dogForms: {
                    ...state.dogForms,
                    [action.dogId]: {
                        ...state.dogForms[action.dogId],
                        ...action.payload,
                    },
                },
            };
        }

        case "SET_ACTIVE_DOG": {
            return { ...state, activeDogId: action.dogId };
        }

        case "SET_DOG_STEP": {
            return {
                ...state,
                stepState: {
                    ...state.stepState,
                    perDogStep: {
                        ...state.stepState.perDogStep,
                        [action.dogId]: action.step,
                    },
                },
            };
        }

        case "SET_GLOBAL_STEP": {
            return {
                ...state,
                stepState: {
                    ...state.stepState,
                    globalStep: action.step,
                },
            };
        }

        case "SET_TREATS": {
            return {
                ...state,
                treatSelections: {
                    ...state.treatSelections,
                    [action.dogId]: action.payload,
                },
            };
        }

        case "SET_RESULTS_STEP": {
            return {
                ...state,
                resultsStep: action.step,
            };
        }

        case "ADD_DOG": {
            // Same cap SET_DOGS_COUNT enforces — "+ Add another dog" must not
            // grow the household past it.
            if (state.dogOrder.length >= MAX_DOGS) {
                return state;
            }

            const newId = safeUUID();
            const newDogOrder = [...state.dogOrder, newId];

            return {
                ...state,
                dogOrder: newDogOrder,
                dogForms: {
                    ...state.dogForms,
                    [newId]: createBlankDogForm(),
                },
                activeDogId: newId,
                stepState: {
                    ...state.stepState,
                    perDogStep: {
                        ...state.stepState.perDogStep,
                        [newId]: "basic",
                    },
                },
            };
        }

        case "REMOVE_DOG": {
            if (state.dogOrder.length <= 1) {
                return state;
            }

            const { dogId } = action;
            const newDogOrder = state.dogOrder.filter((id) => id !== dogId);

            if (newDogOrder.length === 0) {
                return state;
            }

            const { [dogId]: _removedForm, ...restForms } = state.dogForms;
            const { [dogId]: _removedStep, ...restSteps } = state.stepState.perDogStep;
            const { [dogId]: _removedTreats, ...restTreats } =
                state.treatSelections ?? {};
            const newActiveDogId =
                state.activeDogId === dogId ? newDogOrder[0] ?? null : state.activeDogId;

            return {
                ...state,
                dogOrder: newDogOrder,
                dogForms: restForms,
                activeDogId: newActiveDogId,
                stepState: {
                    ...state.stepState,
                    perDogStep: restSteps,
                },
                treatSelections: restTreats,
            };
        }

        case "SET_DOGS_COUNT": {
            // Resize the dogs list to `count` (1–MAX_DOGS): append blank dogs or trim
            // from the end (earlier dogs keep their data). Marks the count screen as answered.
            const count = Math.max(1, Math.min(MAX_DOGS, Math.round(action.count)));
            let dogOrder = [...state.dogOrder];
            const dogForms = { ...state.dogForms };
            const perDogStep = { ...state.stepState.perDogStep };

            while (dogOrder.length < count) {
                const newId = safeUUID();
                dogOrder.push(newId);
                dogForms[newId] = createBlankDogForm();
                perDogStep[newId] = state.stepState.perDogStep[state.dogOrder[0]] ?? "basic";
            }
            if (dogOrder.length > count) {
                const removed = dogOrder.slice(count);
                dogOrder = dogOrder.slice(0, count);
                removed.forEach((id) => {
                    delete dogForms[id];
                    delete perDogStep[id];
                });
            }

            const activeDogId =
                state.activeDogId && dogOrder.includes(state.activeDogId)
                    ? state.activeDogId
                    : dogOrder[0] ?? null;

            return {
                ...state,
                dogOrder,
                dogForms,
                activeDogId,
                dogsCountConfirmed: true,
                stepState: { ...state.stepState, perDogStep },
            };
        }

        case "SET_DELIVERY_DATES":
            return {
                ...state,
                deliveryDate: action.deliveryDate,
                shipDate: action.shipDate,
            };

        default:
            return state;
    }
}

export function QuizProvider({ children, initial }: { children: ReactNode; initial: QuizState }) {
    const [state, dispatch] = useReducer(quizReducer, initial);
    return (
        <QuizContext.Provider value={{ state, dispatch }}>
            {children}
        </QuizContext.Provider>
    );
}

export function useQuizContext() {
    const ctx = useContext(QuizContext);
    if (!ctx) throw new Error("QuizProvider missing");
    return ctx;
}
